import { getActiveOrgId } from '@/stores/orgStore';
import { useAuthStore } from '@/stores/authStore';
import { getLocale } from '@/lib/i18n';

type ApiInit = RequestInit & {
  skipRefresh?: boolean;
  skipRedirect?: boolean; // Don't redirect to /auth on 401
};

let refreshingPromise: Promise<void> | null = null;
let refreshAttempts = 0;
let lastRefreshTime = 0;
const MAX_REFRESH_ATTEMPTS = 3;
const REFRESH_COOLDOWN = 5000; // 5 seconds

// Central redirect hook – swaps to router navigation in the app shell when available
function navigateToAuth() {
  try {
    // Fallback for now; can be replaced by a router-aware helper
    window.location.replace('/auth');
  } catch {}
}

// Remove ad-hoc debug/event logging in production build

// Circuit breaker for refresh attempts
function canAttemptRefresh(): boolean {
  const now = Date.now();
  if (now - lastRefreshTime > REFRESH_COOLDOWN) {
    refreshAttempts = 0; // Reset counter after cooldown
  }
  
  if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
    return false;
  }
  return true;
}

function isAuthEndpoint(url: string): boolean {
  try {
    const u = new URL(url, window.location.origin);
    // Skip refresh for auth endpoints to avoid loops
    // NOTE: session/bootstrap is NOT included here because we want
    // it to attempt refresh when access token expires
    return u.pathname === '/api/auth/refresh' || 
           u.pathname === '/api/auth/login' ||
           u.pathname === '/api/auth/logout';
  } catch {
    return false;
  }
}

// API Base URL for cross-origin deployments
// If VITE_API_URL is set, prepend it to relative paths
// If empty/undefined, use relative paths (same-origin)
const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || '';

// Forward declaration - refreshSession will be implemented after apiFetch
let refreshSession: (skipRedirect?: boolean) => Promise<void>;

export async function apiFetch(input: RequestInfo | URL, init?: ApiInit): Promise<Response> {
  // Build the final URL
  let url: string;
  if (typeof input === 'string') {
    // Only prepend base URL for relative paths starting with /
    url = input.startsWith('/') && API_BASE_URL ? `${API_BASE_URL}${input}` : input;
  } else if (input instanceof URL) {
    url = input.toString();
  } else {
    url = String((input as any)?.url || input);
  }

  const originalUrl = url;

  const headers = new Headers(init?.headers || {});
  
  // Add Content-Type for requests with body (if not already set)
  // Skip for FormData - browser will set multipart/form-data with boundary automatically
  if (init?.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  
  // Add org header if not present and we have an active org
  if (!headers.has('X-Organization-Id')) {
    const orgId = getActiveOrgId();
    if (orgId) {
      headers.set('X-Organization-Id', orgId);
    }
  }

  // Forward the active UI locale so the server can translate error messages
  // to match the user's selected language. Backend `localeMiddleware`
  // (server/http/middleware/locale.ts) parses Accept-Language with the same
  // resolution chain used by the frontend i18n. Caller-provided header wins.
  if (!headers.has('Accept-Language')) {
    try {
      const uiLocale = getLocale();
      if (uiLocale) headers.set('Accept-Language', uiLocale);
    } catch {
      // i18n module not initialised yet (e.g. very early bootstrap call).
      // Skip silently — server falls back to org default_language.
    }
  }

  const requestInit: RequestInit = {
    ...init,
    headers,
    credentials: init?.credentials || 'include',
  };

  const res = await fetch(url, requestInit);
  
  if (res.status !== 401) {
    return res;
  }

  // 401 handling: do not try to refresh for auth endpoints or when explicitly skipped
  if (init?.skipRefresh || isAuthEndpoint(originalUrl)) {
    return res;
  }

  // Note: We can't check httpOnly cookies from JavaScript, so we'll attempt refresh
  // and let the server tell us if there's no valid refresh token

  // Circuit breaker: check if we should attempt refresh
  if (!canAttemptRefresh()) {
    // Clear auth state to prevent stale data
    try { useAuthStore.setState({ user: null }); } catch {}
    return res;
  }

  // Attempt a single, deduped refresh
  try {
    await refreshSession(init?.skipRedirect);
  } catch {
    // Refresh failed - clear attempts counter to allow retry later
    refreshAttempts = 0;
    return res; // refreshSession already redirected; return original 401 to caller
  }

  // Replay the original request once after successful refresh
  const replayRes = await fetch(url, requestInit);
  return replayRes;
}

// Implement refreshSession after apiFetch is defined
// Uses apiFetch with skipRefresh: true to avoid circular dependency
refreshSession = async function(skipRedirect = false): Promise<void> {
  if (refreshingPromise) {
    return refreshingPromise;
  }
  
  refreshAttempts++;
  lastRefreshTime = Date.now();
  
  refreshingPromise = (async () => {
    try {
      // Use apiFetch with skipRefresh: true to avoid circular dependency
      // This ensures we respect VITE_API_URL and handle URLs consistently
      const res = await apiFetch('/api/auth/refresh', {
        method: 'POST',
        skipRefresh: true, // Critical: prevents infinite loop since apiFetch calls refreshSession
        skipRedirect: skipRedirect, // Respect the skipRedirect parameter
      });
      
      if (!res.ok) {
        throw new Error('Refresh failed');
      }
      refreshAttempts = 0; // Reset on success
      // Optionally update auth store with returned user
      try {
        const payload = await res.json();
        const me = payload?.data;
        if (me) {
          useAuthStore.setState({ user: me });
        }
      } catch {}
    } catch (e) {
      // Clear auth but only redirect if we're not already on auth page
      try { useAuthStore.setState({ user: null }); } catch {}
      
      // Only redirect if not explicitly skipped and not already on auth page
      // Also never redirect from public share links (/r/ or /p/)
      const currentPath = window.location.pathname;
      const isPublicShareLink = currentPath.startsWith('/r/') || currentPath.startsWith('/p/');
      if (!skipRedirect && !currentPath.startsWith('/auth') && !isPublicShareLink) {
        navigateToAuth();
      }
      throw e;
    } finally {
      refreshingPromise = null;
    }
  })();
  return refreshingPromise;
};


